import squarefour from '../../../img/squarefour.svg';
import commomCheck from '../../../img/commom-check.svg';
import trophySilver from '../../../img/trophy-silver.svg';
import contextBlue from '../../../img/context-blue.svg';
import decisionBlue from '../../../img/decision-blue.svg';
import objectiveBlue from '../../../img/objective-blue.svg';
import { QuestionBlockStatus, LectureQuestion, ReviewResponse } from '../types';

export const informationsItemsArray = [
  {
    icon: squarefour,
    title: '%%questionsQuantities%% questões',
  },
  {
    icon: commomCheck,
    title: 'Escolha apenas uma opção',
  },
  {
    icon: trophySilver,
    title: 'Acerte no mínimo 70% para passar',
  },
];

export const informationsItemsArrayDifQuestions = [
  {
    icon: objectiveBlue,
    title: 'Questões objetivas',
    description: 'Escolha única entre alternativas',
  },
  {
    icon: decisionBlue,
    title: 'Questões de árvore de decisão (multipla escolha)',
    description: 'Resposta escrita (correção automática binária)',
  },
  {
    icon: contextBlue,
    title: 'Questões dissertativas',
    description: 'Resposta escrita (correção automática binária)',
  },
];

// Id do container rolável dos chips de questões. Compartilhado entre o LecturesHeaderPanel
// (que declara o elemento) e a página Lectures (que mede e rola o elemento).
export const QUESTIONS_SELECT_AREA_ID = 'select-questions-area';

// Passo (em px) de rolagem ao clicar nas setas de navegação dos chips
export const QUESTIONS_SELECT_SCROLL_STEP = 300;

export const itemsSelectOnReview = [
  {
    title: 'Todas questões',
    value: 'all',
  },
  {
    title: 'Somente questões corretas',
    value: 'correct',
  },
  {
    title: 'Somente questões erradas',
    value: 'wrong',
  },
];

export type ItemSelectOnReview = {
  title: string;
  value: 'all' | 'correct' | 'wrong';
};

export const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

export const parseTimeToSeconds = (timeString: string): number => {
  const parts = timeString.split(':');
  if (parts.length === 3) {
    const hrs = parseInt(parts[0], 10) || 0;
    const mins = parseInt(parts[1], 10) || 0;
    const secs = parseInt(parts[2], 10) || 0;
    return hrs * 3600 + mins * 60 + secs;
  }
  return 0;
};

export const parseTimeStringToSeconds = (timeStr: string): number => {
  const timeParts = timeStr.split(':');
  if (timeParts.length !== 3) return 0;
  return parseInt(timeParts[0]) * 3600 + parseInt(timeParts[1]) * 60 + parseInt(timeParts[2]);
};

export const getBlockCtaLabel = (status: QuestionBlockStatus): string => {
  if (status === 'completed') return 'Refazer bloco';
  if (status === 'in_progress') return 'Continuar bloco';
  return 'Iniciar bloco';
};

/**
 * `answered_questions` reflete o rascunho (autosave) em aberto, não o histórico do bloco — o
 * backend zera esse contador assim que a prova é finalizada e o rascunho é limpo. Por isso, um
 * bloco `completed` sem rascunho ativo chega com `answered_questions: 0`, mesmo tendo sido
 * respondido por completo. Quando há uma nova tentativa em andamento (rascunho > 0), esse valor
 * já reflete o progresso real da tentativa atual e deve ser exibido como está.
 */
export const getDisplayedAnsweredQuestions = (block: {
  status: QuestionBlockStatus;
  answered_questions: number;
  total_questions: number;
}): number =>
  block.status === 'completed' && block.answered_questions === 0
    ? block.total_questions
    : block.answered_questions;

/**
 * O backend mantém `status: 'completed'` para sempre após a primeira prova finalizada do bloco
 * (por decisão do PO, já que as tentativas são ilimitadas). Mas ao refazer um bloco já concluído
 * e parar antes de terminar, existe um rascunho ativo (`answered_questions > 0`) — nesse caso a
 * UI deve mostrar "em andamento", não "concluído", para não sugerir que a tentativa atual já
 * terminou.
 */
export const getDisplayedBlockStatus = (block: {
  status: QuestionBlockStatus;
  answered_questions: number;
}): QuestionBlockStatus =>
  block.status === 'completed' && block.answered_questions > 0 ? 'in_progress' : block.status;

export type BlockOrigin = 'block' | 'whole' | null;

/**
 * Decide o alvo do botão "Sair": volta para a lista de blocos sempre que o aluno chegou até aqui
 * a partir de um bloco específico OU da opção "responder a prova inteira" — nesses dois casos
 * `activeBlockId` sozinho não basta (é `null` tanto na lista quanto no fluxo de prova inteira).
 * Fora do modo blocos, ou quando não há origem registrada (ex.: revisão aberta direto da tela do
 * curso), o destino é a tela do curso.
 */
export const shouldExitToBlocksList = (blocksMode: boolean, blockOrigin: BlockOrigin): boolean =>
  blocksMode && blockOrigin !== null;

export const buildFinishLecturePayload = <T extends { lesson_id: number }>(
  base: T,
  blockId?: number | null,
) => (blockId != null ? { ...base, block_id: blockId } : base);

/**
 * Restringe às questões que de fato fazem parte da prova revisada. O endpoint de detalhes da
 * aula sempre retorna o array plano da aula inteira, mesmo quando a prova revisada foi de um
 * bloco específico — sem esse filtro, a revisão mostraria todas as questões da aula.
 */
export const scopeQuestionsToReview = (
  questions: LectureQuestion[],
  reviewData: ReviewResponse,
): LectureQuestion[] => {
  const reviewedQuestionIds = new Set(reviewData.questions.map((q) => q.question_id));
  return questions.filter((q) => reviewedQuestionIds.has(q.id_questao));
};
